import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getMe } from '../api/me';
import { localLogin, logout as apiLogout } from '../api/auth';
import { getStoredToken, setStoredToken } from '../api/client';
import type { MeResponse } from '../api/types';
import { AuthContext, type AuthContextValue, type AuthStatus } from './authContextValue';

interface AuthState {
  status: AuthStatus;
  me: MeResponse | null;
  error: string | null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: 'initializing',
    me: null,
    error: null,
  });

  // Evita que o segundo disparo do useEffect (React StrictMode em desenvolvimento)
  // chame refresh() sem token enquanto o exchange do OTP ainda está em andamento.
  const exchangePending = useRef(false);

  const refresh = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setState({ status: 'unauthenticated', me: null, error: null });
      return;
    }
    try {
      const me = await getMe();
      setState({ status: 'authenticated', me, error: null });
    } catch (err) {
      setStoredToken(null);
      setState({ status: 'unauthenticated', me: null, error: (err as Error).message });
    }
  }, []);

  useEffect(() => {
    // Listener sempre registrado — independente do fluxo de login.
    // Sem isso, tokens expirados não disparariam o logout automático
    // quando o usuário chegasse via callback Entra (path do code).
    const onUnauthorized = () => {
      setStoredToken(null);
      setState({ status: 'unauthenticated', me: null, error: 'Sessao expirada' });
    };
    window.addEventListener('portal:unauthorized', onUnauthorized);

    // Lida com retorno do callback Entra OAuth:
    // backend redireciona para /auth/callback?code=<otp> (nunca expõe o JWT na URL).
    const params = new URLSearchParams(window.location.search);
    const code  = params.get('code');
    const error = params.get('error');

    if (code) {
      window.history.replaceState({}, '', window.location.pathname);
      exchangePending.current = true;
      fetch('/api/auth/session/exchange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      })
        .then(r => r.json())
        .then((data: { accessToken?: string }) => {
          if (data.accessToken) setStoredToken(data.accessToken);
        })
        .catch(() => { /* erro tratado pelo refresh que segue */ })
        .finally(() => {
          exchangePending.current = false;
          void refresh();
        });
    } else if (!exchangePending.current) {
      // Não chama refresh() se um exchange já está em andamento —
      // evita que o segundo disparo do StrictMode defina status='unauthenticated'
      // antes de o fetch completar, causando flash da tela de login.
      if (error) window.history.replaceState({}, '', window.location.pathname);
      void refresh();
    }

    return () => window.removeEventListener('portal:unauthorized', onUnauthorized);
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      await localLogin(email, password);
      await refresh();
    },
    [refresh],
  );

  // Redireciona ao backend que inicia o fluxo OAuth com a Microsoft.
  // Nao usa MSAL no frontend — sem SPA configurado no Azure AD necessario.
  const loginWithEntra = useCallback(async () => {
    window.location.href = '/api/auth/entra/login';
  }, []);

  const logout = useCallback(() => {
    apiLogout();
    setState({ status: 'unauthenticated', me: null, error: null });
  }, []);

  const hasPermission = useCallback(
    (key: string): boolean => {
      const perms = state.me?.permissions ?? [];
      return perms.includes('system.full_access') || perms.includes(key);
    },
    [state.me],
  );

  const hasAnyPermission = useCallback(
    (...keys: string[]): boolean => {
      const perms = state.me?.permissions ?? [];
      if (perms.includes('system.full_access')) return true;
      return keys.some((k) => perms.includes(k));
    },
    [state.me],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, loginWithEntra, logout, refresh, hasPermission, hasAnyPermission }),
    [state, login, loginWithEntra, logout, refresh, hasPermission, hasAnyPermission],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
